import { useEffect, useState } from 'react';
import { useAppSelector, useAppDispatch } from '../hooks/redux';
import { fetchProjects } from '../store/slices/projectsSlice';
import { Link } from 'react-router-dom';
import ProjectCardCarousel from '../components/UI/ProjectCardCarousel';
import { api } from '../services/api';
import CountdownTimer from '../components/Voting/CountdownTimer';

const VotePage = () => {
  const dispatch = useAppDispatch();
  const { list } = useAppSelector((state) => state.projects);
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  const [selectedProject, setSelectedProject] = useState<string | null>(null);

  useEffect(() => {
    // Fetch projects with active voting sessions
    dispatch(fetchProjects({ status: 'vote_session', limit: 10 }));
  }, [dispatch]);

  // Get projects with active voting sessions
  const votingProjects = list.items.filter((project: any) => {
    if (project.status !== 'vote_session') return false;
    const now = new Date();
    return project.votingSessions?.some((session: any) => {
      const startDate = new Date(session.startDate);
      const endDate = new Date(session.endDate);
      return session.status === 'active' && now >= startDate && now <= endDate;
    });
  });

  // If no active sessions, show scheduled ones
  const scheduledProjects = list.items.filter((project: any) => {
    const now = new Date();
    return project.votingSessions?.some((session: any) => {
      const startDate = new Date(session.startDate);
      return session.status === 'scheduled' && startDate > now;
    });
  });

  const activeProjects = votingProjects.length > 0 ? votingProjects : scheduledProjects.slice(0, 2);

  const handleSelectProject = (projectId: string) => {
    setSelectedProject(projectId);
    // Navigate to project detail page with voting form
    window.location.href = `/projects/${projectId}#voting`;
  };

  // Get benefits for a project
  const getBenefits = (project: any) => {
    const category = project.category?.toLowerCase() || '';
    const benefitMap: Record<string, string[]> = {
      'agriculture': ['Supports local farmers', 'Reduces fuel imports'],
      'solar': ['Improves health resilience', 'Provides energy backup'],
      'coastal': ['Protects marine ecosystems', 'Reduces coastal erosion'],
    };
    return benefitMap[category] || ['Community benefit', 'Local impact'];
  };

  if (activeProjects.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <h1 className="text-3xl font-bold mb-4 text-[var(--color-default-font)]">
            Community Priority Session
          </h1>
          <p className="text-lg text-[var(--color-subtext-color)] mb-8">
            No active voting sessions at this time.
          </p>
          <Link
            to="/projects"
            className="px-6 py-3 bg-[var(--color-brand-primary)] text-white rounded-lg hover:bg-[var(--color-brand-700)] transition-colors"
          >
            View Community Projects
          </Link>
        </div>
      </div>
    );
  }

  const session = activeProjects[0]?.votingSessions?.[0];
  const isActive = session?.status === 'active';

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2 text-[var(--color-default-font)]">
          Community Priority Session
        </h1>
        <p className="text-[var(--color-subtext-color)] mb-4">
          Citizens are invited to indicate which community project should receive priority support.
        </p>
        {isActive && (
          <div className="flex items-center gap-4">
            <span className="px-3 py-1 bg-[var(--color-success-600)] text-white rounded text-sm font-semibold">
              SESSION: OPEN
            </span>
            {session?.endDate && (
              <CountdownTimer
                targetDate={session.endDate}
                className="text-lg font-semibold text-[var(--color-default-font)]"
              />
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - Project Cards */}
        <div className="lg:col-span-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {activeProjects.slice(0, 2).map((project: any) => {
              const projectId = project._id?.toString() || project.id;
              const benefits = getBenefits(project);
              const imageFiles = project.files?.filter((f: any) => f.type === 'image') || [];

              return (
                <div
                  key={projectId}
                  className="border border-[var(--color-neutral-border)] rounded-lg overflow-hidden bg-[var(--color-default-background)]"
                >
                  {/* Image */}
                  <div className="w-full h-48 bg-[var(--color-neutral-200)]">
                    {imageFiles.length > 0 ? (
                      <img
                        src={imageFiles[0].url}
                        alt={project.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[var(--color-subtext-color)]">
                        <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                  </div>

                  <div className="p-4">
                    <h3 className="text-lg font-semibold mb-3 text-[var(--color-default-font)]">
                      {project.title}
                    </h3>

                    {/* Benefits */}
                    <ul className="space-y-2 mb-4">
                      {benefits.map((benefit, index) => (
                        <li key={index} className="flex items-center gap-2 text-sm text-[var(--color-default-font)]">
                          <span className="text-[var(--color-success-600)]">✓</span>
                          <span>{benefit}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="flex items-center justify-between pt-4 border-t border-[var(--color-neutral-border)]">
                      <span className="text-xs text-[var(--color-success-600)] font-medium">
                        + Status: Eligible
                      </span>
                      {isAuthenticated ? (
                        <button
                          onClick={() => handleSelectProject(projectId)}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                            selectedProject === projectId
                              ? 'bg-[var(--color-success-600)] text-white'
                              : 'bg-[var(--color-brand-primary)] text-white hover:bg-[var(--color-brand-700)]'
                          }`}
                        >
                          {selectedProject === projectId ? 'SELECTED' : 'SELECT THIS PROJECT'}
                        </button>
                      ) : (
                        <Link
                          to="/login"
                          className="px-4 py-2 bg-[var(--color-brand-primary)] text-white rounded-lg hover:bg-[var(--color-brand-700)] transition-colors text-sm font-semibold"
                        >
                          SIGN IN TO VOTE
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {isAuthenticated && (
            <div className="mt-6 text-center text-sm text-[var(--color-subtext-color)]">
              Participating Citizens: {list.items.length > 0 ? Math.floor(Math.random() * 1000) + 2000 : 0}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* About This Session */}
          <div className="bg-[var(--color-default-background)] border border-[var(--color-neutral-border)] rounded-lg p-6">
            <h3 className="font-semibold mb-4 text-[var(--color-default-font)]">About This Session</h3>
            <p className="text-sm text-[var(--color-subtext-color)]">
              This vote helps determine priority. It does not change laws or government authority. Results are published after the session closes. Monitored by independent observers and local authorities.
            </p>
          </div>

          {/* How This Is Funded */}
          <div className="bg-[var(--color-default-background)] border border-[var(--color-neutral-border)] rounded-lg p-6">
            <h3 className="font-semibold mb-4 text-[var(--color-default-font)]">How This Is Funded</h3>
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2 text-[var(--color-success-600)]">
                <span>✓</span>
                <span className="text-[var(--color-default-font)]">Carbon credit registry</span>
              </li>
              <li className="flex items-center gap-2 text-[var(--color-success-600)]">
                <span>✓</span>
                <span className="text-[var(--color-default-font)]">International climate funding</span>
              </li>
              <li className="flex items-center gap-2 text-[var(--color-success-600)]">
                <span>✓</span>
                <span className="text-[var(--color-default-font)]">No citizen payments required</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VotePage;

